import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useRecipe } from "../lib/api/queries";
import { useQueryView } from "../lib/loading/useQueryView";
import { PageError } from "./PageError";
import { PageSkeleton } from "./PageSkeleton";
import { Skel } from "./Skel";
import { SkelPanel } from "./skeleton/SkelPanel";

type Recipe = NonNullable<ReturnType<typeof useRecipe>["data"]>;

// A route guard that needs the recipe to decide whether the user may see
// the screen at all. While /recipes/me resolves it never redirects (a
// slow recipe must not bounce a user off a screen they are entitled to)
// and never renders nothing: the body is a vertical-neutral page
// skeleton — the guard doesn't know the screen's layout, so a header bar
// and one panel, every label a bar. It goes through the loading
// contract, so a failed recipe ends in an error with Reintentar, never a
// blank page or an endless skeleton.
export function RecipeGate({
  allow,
  redirectTo,
  children,
}: {
  allow: (recipe: Recipe) => boolean;
  redirectTo: string;
  children: ReactNode;
}) {
  const view = useQueryView(useRecipe());

  if (view.kind === "loading") {
    return (
      <div className="page">
        <PageSkeleton label="Cargando la página…" visible={view.visible}>
          <header className="page-header">
            <h1 className="page-header__title">
              <Skel variant="title" width="28%" />
            </h1>
          </header>
          <SkelPanel />
        </PageSkeleton>
      </div>
    );
  }
  if (view.kind === "error") {
    return (
      <div className="page">
        <div className="panel">
          <PageError
            title={view.offline ? "Sin conexión" : "No pudimos cargar la página"}
            description={
              view.offline
                ? "Esta información aún no está guardada en este dispositivo. Conéctate para verla."
                : "Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
            }
            onRetry={view.retry}
          />
        </div>
      </div>
    );
  }
  // useRecipe is always enabled and has no isEmpty, so `empty`/`disabled`
  // can't happen; if one ever does, no recipe is on its way — treat it as
  // not allowed rather than wait forever.
  if (view.kind !== "ready" || !allow(view.data)) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}
